import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import useSmqPermissions from "../../../hooks/useSmqPermissions";
import SMQMainContainer from "../../../components/SMQMainContainer";
import ProcessusService from "../../../services/smq/ProcessusService";
import ButtonYellow from "../component/BouttonYellow";
import useParameter from "../hooks/useParameter";
import HeaderProcessus from "./HeaderProcessus";
import ProcessusAdd from "./ProcessusAdd";

const EMPTY_DIALOG = { initialValues: null, mode: "create", open: false };

const CATEGORY_COLORS = {
  controle: { background: "#eee6ff", border: "#7d42da", text: "#6f35c5" },
  direction: { background: "#e7eff7", border: "#173d61", text: "#173d61" },
  realisation: { background: "#e4f2e9", border: "#28724a", text: "#28724a" },
  support: { background: "#faeadb", border: "#c55d08", text: "#b45c12" },
};

const normalizeText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("fr")
    .trim();

const sameId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left) === String(right);

const categoryColors = (name) =>
  CATEGORY_COLORS[normalizeText(name)] || {
    background: "#edf1f5",
    border: "#60758a",
    text: "#52677c",
  };

const ProcessusCategoryTable = memo(
  ({ canManage, category, drafts, filterKey, onDraftChange, onEdit, onOpenTools, rows }) => {
    const [paginationModel, setPaginationModel] = useState({
      page: 0,
      pageSize: 10,
    });
    const colors = categoryColors(category.categorie_name);
    const gridRows = useMemo(
      () =>
        rows.map((row) => ({
          ...row,
          valeur: drafts[row.processus_id] ?? "",
        })),
      [drafts, rows]
    );

    useEffect(() => {
      setPaginationModel((current) =>
        current.page === 0 ? current : { ...current, page: 0 }
      );
    }, [filterKey]);

    const columns = useMemo(
      () => [
        {
          field: "instance",
          flex: 0.8,
          headerName: "Identifiant",
          minWidth: 145,
          renderCell: ({ value }) => (
            <Box
              component="span"
              sx={{
                backgroundColor: "#edf3f8",
                borderRadius: "4px",
                color: "#173d61",
                fontFamily: "'DM Mono', monospace",
                fontSize: 11,
                fontWeight: 700,
                px: 0.8,
                py: 0.35,
              }}
            >
              {value || "—"}
            </Box>
          ),
        },
        {
          field: "referentiel_nom",
          flex: 1.3,
          headerName: "Nom",
          minWidth: 190,
          renderCell: ({ value }) => (
            <Typography color="#10263d" fontSize={12} fontWeight={700}>
              {value || "—"}
            </Typography>
          ),
        },
        {
          field: "direction_code",
          flex: 0.65,
          headerName: "Direction",
          minWidth: 125,
          renderCell: ({ value }) => (
            <Box
              component="span"
              sx={{
                backgroundColor: colors.background,
                borderRadius: "999px",
                color: colors.text,
                fontSize: 11,
                fontWeight: 700,
                px: 1,
                py: 0.45,
              }}
            >
              {value || "—"}
            </Box>
          ),
        },
        {
          field: "pilote_name",
          flex: 1.2,
          headerName: "Pilote",
          minWidth: 180,
          valueGetter: (value) => String(value || "").trim() || "—",
        },
        {
          field: "indicateur",
          flex: 1.35,
          headerName: "Indicateurs",
          minWidth: 190,
          renderCell: ({ value }) => (
            <Typography fontSize={12} sx={{ whiteSpace: "pre-wrap" }}>
              {value || "—"}
            </Typography>
          ),
        },
        {
          field: "valeur",
          filterable: true,
          flex: 1.35,
          headerName: "Valeur indicateur",
          minWidth: 210,
          renderCell: ({ row }) => (
            <Box sx={{ py: 0.7, width: "100%" }}>
              <TextField
                inputProps={{
                  "aria-label": `Valeur locale de ${row.instance}`,
                }}
                onChange={(event) =>
                  onDraftChange(row.processus_id, event.target.value)
                }
                placeholder="Saisie libre…"
                size="small"
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": { fontSize: 11, height: 34 },
                }}
                value={row.valeur}
              />
              <Typography color="#789" fontSize={9} sx={{ mt: 0.25 }}>
                Saisie locale non enregistrée
              </Typography>
            </Box>
          ),
          sortable: true,
        },
        {
          align: "center",
          field: "actions",
          filterable: false,
          headerAlign: "center",
          headerName: "Actions",
          minWidth: 165,
          sortable: false,
          width: 165,
          renderCell: ({ row }) => (
            <Box sx={{ alignItems: "center", display: "flex", gap: 0.6 }}>
              {canManage && <Tooltip title="Modifier">
                <IconButton
                  aria-label={`Modifier ${row.instance}`}
                  onClick={() => onEdit(row)}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(23, 61, 97, 0.1)",
                    borderRadius: "8px",
                    color: "#173d61",
                    height: 34,
                    width: 34,
                    "&:hover": { backgroundColor: "rgba(23, 61, 97, 0.18)" },
                  }}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>}
              <Tooltip title="Ouvrir les outils du processus">
                <IconButton
                  aria-label={`Ouvrir les outils de ${row.instance}`}
                  disabled={false}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenTools(row.processus_id);
                  }}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(125, 66, 218, 0.1)",
                    borderRadius: "8px",
                    color: "#6f35c5",
                    cursor: "pointer",
                    height: 34,
                    opacity: 1,
                    pointerEvents: "auto",
                    width: 34,
                    "&:hover": { backgroundColor: "rgba(125, 66, 218, 0.18)" },
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(125, 66, 218, 0.1)",
                      color: "#6f35c5",
                      opacity: 1,
                      pointerEvents: "auto",
                    },
                  }}
                >
                  <BuildOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canManage && <Tooltip title="Suppression bientôt disponible">
                <span>
                  <IconButton
                    aria-label={`Supprimer ${row.instance}`}
                    disabled
                    size="small"
                    sx={{
                      backgroundColor: "rgba(173, 31, 36, 0.1)",
                      borderRadius: "8px",
                      color: "#ad1f24",
                      height: 34,
                      width: 34,
                      "&.Mui-disabled": {
                        backgroundColor: "rgba(173, 31, 36, 0.1)",
                        color: "#ad1f24",
                        opacity: 0.7,
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>}
            </Box>
          ),
        },
      ],
      [canManage, colors.background, colors.text, onDraftChange, onEdit, onOpenTools]
    );

    return (
      <Paper
        elevation={0}
        sx={{ border: "1px solid #cbd8e6", borderRadius: "12px", overflow: "hidden" }}
      >
        <Box
          sx={{
            alignItems: "center",
            backgroundColor: "#edf3f8",
            borderBottom: `2px solid ${colors.border}`,
            display: "flex",
            minHeight: 54,
            px: 2,
          }}
        >
          <Box
            component="span"
            sx={{
              backgroundColor: colors.background,
              borderRadius: "999px",
              color: colors.text,
              fontSize: 12,
              fontWeight: 700,
              px: 1.2,
              py: 0.55,
            }}
          >
            {category.categorie_name}
          </Box>
        </Box>

        <Box sx={{ height: 460, minWidth: 0 }}>
          <DataGrid
            columns={columns}
            disableRowSelectionOnClick
            getRowId={(row) => row.processus_id}
            localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            paginationModel={paginationModel}
            rowHeight={72}
            rows={gridRows}
            sx={{
              border: 0,
              color: "#10263d",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 12,
              "& .MuiDataGrid-cell": { borderColor: "#d5e0eb" },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#173d61",
                color: "#fff",
                fontSize: 11,
                fontWeight: 700,
              },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
                outline: "2px solid #d5ad3f",
                outlineOffset: "-2px",
              },
              "& .MuiDataGrid-columnSeparator, & .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": {
                color: "#fff",
              },
              "& .MuiDataGrid-footerContainer": { borderColor: "#d5e0eb" },
              "& .MuiDataGrid-row:hover": { backgroundColor: "#f6f9fc" },
            }}
          />
        </Box>
      </Paper>
    );
  }
);

ProcessusCategoryTable.displayName = "ProcessusCategoryTable";

const LoadingTables = () => (
  <Box sx={{ display: "grid", gap: 2, mt: 2 }}>
    {Array.from({ length: 4 }, (_, index) => (
      <Skeleton key={index} height={250} sx={{ borderRadius: "12px" }} variant="rectangular" />
    ))}
  </Box>
);

const Processus = () => {
  const { isAdmin: isSmqAdmin } = useSmqPermissions();
  const navigate = useNavigate();
  const [processus, setProcessus] = useState([]);
  const [processusLoading, setProcessusLoading] = useState(true);
  const [processusError, setProcessusError] = useState(null);
  const [formDialog, setFormDialog] = useState(EMPTY_DIALOG);
  const [valueDrafts, setValueDrafts] = useState({});
  const [categoryFilterId, setCategoryFilterId] = useState(null);
  const [directionFilterId, setDirectionFilterId] = useState(null);
  const [notification, setNotification] = useState({
    message: "",
    open: false,
    severity: "success",
  });
  const requestIdRef = useRef(0);
  const {
    categories,
    categoriesError,
    categoriesLoading,
    refreshCategories,
  } = useParameter();

  const loadProcessus = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setProcessusLoading(true);
    setProcessusError(null);

    try {
      const payload = await ProcessusService.getProcessus();
      if (!Array.isArray(payload?.data)) {
        throw new Error("Le service des processus a retourné une réponse invalide");
      }

      if (requestId === requestIdRef.current) {
        setProcessus(payload.data);
        setValueDrafts(
          payload.data.reduce(
            (drafts, row) => ({ ...drafts, [row.processus_id]: row.valeur || "" }),
            {}
          )
        );
      }
    } catch (error) {
      if (requestId === requestIdRef.current) {
        setProcessus([]);
        setProcessusError(error?.message || "Impossible de charger les processus");
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setProcessusLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadProcessus();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadProcessus]);

  const orderedCategories = useMemo(
    () =>
      [...categories].sort((left, right) => {
        const leftOrder = Number(left.categorie_ordre);
        const rightOrder = Number(right.categorie_ordre);
        return (Number.isFinite(leftOrder) ? leftOrder : Number.MAX_SAFE_INTEGER) -
          (Number.isFinite(rightOrder) ? rightOrder : Number.MAX_SAFE_INTEGER);
      }),
    [categories]
  );

  const categoryOptions = useMemo(
    () =>
      orderedCategories.map((category) => ({
        id: category.categorie_id,
        label: category.categorie_name || "Catégorie sans nom",
      })),
    [orderedCategories]
  );

  const directionOptions = useMemo(() => {
    const optionsById = new Map();

    processus.forEach((row) => {
      if (row.direction_id === null || row.direction_id === undefined) {
        return;
      }

      const key = String(row.direction_id);
      if (!optionsById.has(key)) {
        const code = String(row.direction_code || "").trim();
        const name = String(row.direction_name || "").trim();
        optionsById.set(key, {
          id: row.direction_id,
          label: [code, name].filter(Boolean).join(" — ") || `Direction ${key}`,
        });
      }
    });

    return [...optionsById.values()].sort((left, right) =>
      left.label.localeCompare(right.label, "fr", { sensitivity: "base" })
    );
  }, [processus]);

  const selectedCategory = useMemo(
    () => categoryOptions.find((option) => sameId(option.id, categoryFilterId)) || null,
    [categoryFilterId, categoryOptions]
  );
  const selectedDirection = useMemo(
    () => directionOptions.find((option) => sameId(option.id, directionFilterId)) || null,
    [directionFilterId, directionOptions]
  );

  useEffect(() => {
    if (categoryFilterId !== null && !selectedCategory) {
      setCategoryFilterId(null);
    }
  }, [categoryFilterId, selectedCategory]);

  useEffect(() => {
    if (directionFilterId !== null && !selectedDirection) {
      setDirectionFilterId(null);
    }
  }, [directionFilterId, selectedDirection]);

  const visibleCategories = useMemo(
    () =>
      categoryFilterId === null
        ? orderedCategories
        : orderedCategories.filter((category) =>
            sameId(category.categorie_id, categoryFilterId)
          ),
    [categoryFilterId, orderedCategories]
  );

  const filteredProcessus = useMemo(
    () =>
      processus.filter(
        (row) =>
          (categoryFilterId === null ||
            sameId(row.categorie_id, categoryFilterId)) &&
          (directionFilterId === null ||
            sameId(row.direction_id, directionFilterId))
      ),
    [categoryFilterId, directionFilterId, processus]
  );

  const filterKey = `${categoryFilterId ?? "all"}:${directionFilterId ?? "all"}`;
  const hasActiveFilters = categoryFilterId !== null || directionFilterId !== null;

  const resetFilters = () => {
    setCategoryFilterId(null);
    setDirectionFilterId(null);
  };

  const openCreateDialog = () => {
    if (!isSmqAdmin) return;
    setFormDialog({ initialValues: null, mode: "create", open: true });
  };

  const openEditDialog = (row) => {
    if (!isSmqAdmin) return;
    setFormDialog({ initialValues: { ...row }, mode: "edit", open: true });
  };

  const openProcessusTools = (processusId) => {
    navigate(`/smq_nc/processus/outils/${processusId}`);
  };

  const closeDialog = () => setFormDialog((current) => ({ ...current, open: false }));

  const handleSaved = async () => {
    setNotification({
      message:
        formDialog.mode === "edit"
          ? "Processus modifié avec succès."
          : "Processus créé avec succès.",
      open: true,
      severity: "success",
    });
    await loadProcessus();
  };

  const retryAll = () => {
    loadProcessus();
    if (categoriesError) {
      refreshCategories().catch(() => undefined);
    }
  };

  const loading = processusLoading || (categoriesLoading && categories.length === 0);
  const error = processusError || (categories.length === 0 ? categoriesError : null);

  return (
    <SMQMainContainer>
      <>
        <HeaderProcessus
          icon={
            <SettingsOutlinedIcon
              aria-hidden="true"
              sx={{ color: "#d4a733", fontSize: 16 }}
            />
          }
          title="Processus"
        />

        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 3 }}>
          <Grid alignItems="center" container justifyContent="space-between" spacing={2}>
            <Grid item sm={6} xs={12}>
              <Typography color="#10263d" fontWeight={700} gutterBottom variant="h6">
                Gestion des Processus
              </Typography>
              <Typography color="#637381" fontSize={14} variant="body2">
                Tous les processus FID — organisation, fiches, indicateurs
              </Typography>
            </Grid>
            <Grid
              item
              sm={6}
              sx={{ display: "flex", justifyContent: { xs: "flex-start", sm: "flex-end" } }}
              xs={12}
            >
              {isSmqAdmin && (
                <ButtonYellow onClick={openCreateDialog}>+ Nouveau Processus</ButtonYellow>
              )}
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              alignItems: { sm: "center" },
              border: "1px solid #cbd8e6",
              borderRadius: "8px",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              mt: 1,
              p: 1.25,
            }}
          >
            <Typography
              color="#52677c"
              fontSize={10}
              fontWeight={700}
              letterSpacing="0.05em"
              sx={{ flexShrink: 0, mr: 0.25 }}
            >
              FILTRES :
            </Typography>
            <Autocomplete
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
              onChange={(_, option) => setCategoryFilterId(option?.id ?? null)}
              options={categoryOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    "aria-label": "Filtrer par catégorie",
                  }}
                  placeholder="Toutes les catégories"
                  size="small"
                />
              )}
              sx={{
                width: { xs: "100%", sm: 400 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  fontSize: 12,
                  height: 36,
                  py: "0 !important",
                },
                "& .MuiAutocomplete-input": {
                  py: "0 !important",
                },
              }}
              value={selectedCategory}
            />
            <Autocomplete
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
              onChange={(_, option) => setDirectionFilterId(option?.id ?? null)}
              options={directionOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    "aria-label": "Filtrer par direction",
                  }}
                  placeholder="Toutes les directions"
                  size="small"
                />
              )}
              sx={{
                width: { xs: "100%", sm: 400 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  fontSize: 12,
                  height: 36,
                  py: "0 !important",
                },
                "& .MuiAutocomplete-input": {
                  py: "0 !important",
                },
              }}
              value={selectedDirection}
            />
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                gap: 1.5,
                justifyContent: { xs: "space-between", sm: "flex-end" },
                ml: { sm: "auto" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              
              <Button
                color="inherit"
                disabled={!hasActiveFilters}
                onClick={resetFilters}
                size="small"
                startIcon={<RestartAltIcon />}
                sx={{
                  borderColor: "#9fb1c3",
                  color: "#173d61",
                  fontSize: 11,
                  fontWeight: 700,
                  minWidth: 118,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#f3f7fa",
                    borderColor: "#173d61",
                  },
                }}
                variant="outlined"
              >
                Réinitialiser
              </Button>
            </Box>
          </Paper>

          {error && (
            <Alert
              action={
                <Button color="inherit" onClick={retryAll} size="small">
                  Réessayer
                </Button>
              }
              severity="error"
              sx={{ mt: 2 }}
            >
              {error}
            </Alert>
          )}

          {loading ? (
            <LoadingTables />
          ) : !error && orderedCategories.length > 0 ? (
            <Box sx={{ display: "grid", gap: 2, mt: 2 }}>
              {visibleCategories.map((category) => (
                <ProcessusCategoryTable
                  canManage={isSmqAdmin}
                  category={category}
                  drafts={valueDrafts}
                  filterKey={filterKey}
                  key={category.categorie_id}
                  onDraftChange={(processusId, value) =>
                    setValueDrafts((current) => ({ ...current, [processusId]: value }))
                  }
                  onEdit={openEditDialog}
                  onOpenTools={openProcessusTools}
                  rows={filteredProcessus.filter((row) =>
                    sameId(row.categorie_id, category.categorie_id)
                  )}
                />
              ))}
            </Box>
          ) : !error ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Aucune catégorie de processus disponible.
            </Alert>
          ) : null}
        </Box>

        <ProcessusAdd
          initialValues={formDialog.initialValues}
          mode={formDialog.mode}
          onClose={closeDialog}
          onSaved={handleSaved}
          open={formDialog.open}
        />

        <Snackbar
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          autoHideDuration={4000}
          onClose={() => setNotification((current) => ({ ...current, open: false }))}
          open={notification.open}
        >
          <Alert
            onClose={() => setNotification((current) => ({ ...current, open: false }))}
            severity={notification.severity}
            variant="filled"
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </>
    </SMQMainContainer>
  );
};

export default Processus;
